/* =========================================================================
   Change Orders — List (v2)
   Ported from docs/featureBriefs/design_handoff_work_orders_change_orders/prototype/app/work-orders.js
   (PAGES["change-orders"]). Rows link to the CO detail page.
   ========================================================================= */
import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, Clock, Filter, Layers, Plus, Search } from "lucide-react";
import "./WorkOrders.css";
import { useSiteContext } from "../../../SiteContext";
import { Page } from "../../../interfaces/PagesInterface";
import ChangeOrdersManager from "../../../managers/ChangeOrdersManager";
import { ChangeOrderListItem } from "../../../interfaces/ChangeOrderInterface";
import { Banner, EmptyState, PageHead, StatusPill, fmtDate, money } from "./woShared";

const STATUS_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: "Open" },
    { value: 2, label: "Completed" },
    { value: 3, label: "Voided" },
    { value: 4, label: "No Charge" },
];

const ChangeOrdersPage: React.FC = () => {
    const { setPage } = useSiteContext();

    const [search, setSearch] = useState(ChangeOrdersManager.selectedSearch || "");
    const [statusFilter, setStatusFilter] = useState<number | "">(ChangeOrdersManager.selectedStatusFilter ?? "");
    const [rows, setRows] = useState<ChangeOrderListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchRows = useCallback(async (status: number | "", q: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await ChangeOrdersManager.getChangeOrders(status === "" ? undefined : status, q.trim() || undefined);
            setRows(data || []);
        } catch (err) {
            console.error("Error fetching change orders", err);
            setError("Failed to load change orders.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Server-side status + search (the API accepts both params) — debounced so
    // typing doesn't fire a request per keystroke; status changes ride the same
    // debounce for simplicity, the delay is short enough not to be noticeable.
    useEffect(() => {
        ChangeOrdersManager.selectedStatusFilter = statusFilter;
        ChangeOrdersManager.selectedSearch = search;
        const t = window.setTimeout(() => fetchRows(statusFilter, search), 300);
        return () => window.clearTimeout(t);
    }, [statusFilter, search, fetchRows]);

    const goCreate = () => {
        // Prototype's App.coCreate resets the job field on every fresh create.
        ChangeOrdersManager.selectedJobNumber = "";
        setPage(Page.ChangeOrderCreate);
    };

    const openDetail = (id: number) => {
        ChangeOrdersManager.selectedChangeOrderID = id;
        setPage(Page.ChangeOrderDetail);
    };

    return (
        <div className="wov2">
            <PageHead
                Icon={Layers}
                title="Change Orders"
                sub="Batches of work orders rolled up for a job so the builder can issue one CO number."
                actions={
                    <button className="btn primary" onClick={goCreate} type="button">
                        <Plus size={15} />Create Change Order
                    </button>
                }
            />

            <div className="toolbar">
                <div className="searchwrap">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Search by job # or project…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <span className="inline-field">
                    <Filter size={14} />
                    <select
                        className="select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value === "" ? "" : Number(e.target.value))}
                    >
                        <option value="">All statuses</option>
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </span>
            </div>

            {error && (
                <Banner
                    variant="danger"
                    Icon={AlertTriangle}
                    action={<button className="btn sm" onClick={() => fetchRows(statusFilter, search)} type="button">Retry</button>}
                >
                    {error}
                </Banner>
            )}

            <div className="card">
                <div className="tbl-wrap">
                    <table className="dt">
                        <thead>
                            <tr>
                                <th>COR # / Job</th>
                                <th>Project</th>
                                <th>Status</th>
                                <th className="cen">Work Orders</th>
                                <th className="num">Total Amount</th>
                                <th>Created</th>
                                <th className="cen">CO# Readiness</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7}><div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading change orders…</div></td></tr>
                            ) : rows.length ? (
                                rows.map((co) => (
                                    <tr
                                        key={co.changeOrderID}
                                        tabIndex={0}
                                        role="button"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => openDetail(co.changeOrderID)}
                                        onKeyDown={(e) => { if (e.key === "Enter") openDetail(co.changeOrderID); }}
                                    >
                                        <td className="strong">
                                            <span className="code">COR #{co.corNumber}</span>
                                            <span className="sub">Job #{co.jobNumber}</span>
                                        </td>
                                        <td>{co.projectName}</td>
                                        <td><StatusPill kind="co" status={co.status} /></td>
                                        <td className="cen">{co.workOrderCount}</td>
                                        <td className="num strong">{money(co.totalAmount)}</td>
                                        <td>{fmtDate(co.createdAt)}</td>
                                        <td className="cen">
                                            {co.status === 1 && co.workOrderCount > 0 ? (
                                                co.missingCONumberCount === 0
                                                    ? <span className="pill st-inv"><Check size={12} />Ready</span>
                                                    : <span className="pill st-sign"><Clock size={12} />{co.missingCONumberCount} missing CO#</span>
                                            ) : <span className="dash">–</span>}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={7}>
                                    <EmptyState Icon={Layers} title="No change orders found">
                                        Adjust the status filter or search, or create a new change order.
                                    </EmptyState>
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ChangeOrdersPage;
